import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { translateErrorCategory } from "@/utils/errorTranslation";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Layers, GraduationCap, PlusCircle, Check, Trash2, ArrowLeft, Filter, Loader2, Eye, EyeOff, Search, BookOpen, Zap, Sparkles, Compass, RefreshCw } from "lucide-react";
import { ContextModal } from "@/components/ui/ContextModal";
import { flashcardService, normalizeFlashcardDifficulty, getFlashcardDifficultyLabel, getFlashcardDifficultyBadgeClass, isWritingV2Flashcard } from "@/services/flashcardService";
import { frenchVerbFlashcards } from "@/data/frenchVerbFlashcards";
import { frenchConnectorFlashcards } from "@/data/frenchConnectorFlashcards";
import { frenchPrepositionFlashcards } from "@/data/frenchPrepositionFlashcards";

export default function Flashcards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDiff = searchParams.get("difficulty");

  const [cards, setCards] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);

  // 6 modos de estudo: 'all' | 'words' | 'verbs' | 'connectors' | 'prepositions' | 'writing_v2'
  const [selectedStudyType, setSelectedStudyType] = useState<'all' | 'words' | 'verbs' | 'connectors' | 'prepositions' | 'writing_v2'>('all');

  // Estatísticas padronizadas (Total, Pendentes Hoje, Novos, Revisados, byRating)
  const [stats, setStats] = useState<{ total: number; dueToday: number; newCards: number; reviewed: number; byRating: { again: number; hard: number; good: number; easy: number } }>({
    total: 0, dueToday: 0, newCards: 0, reviewed: 0, byRating: { again: 0, hard: 0, good: 0, easy: 0 }
  });

  // Estatísticas centralizadas por deck
  type DeckStatType = { total: number; dueToday: number; newToday: number; reviewedToday: number; byRating: { again: number; hard: number; good: number; easy: number } };
  const defaultByRating = { again: 0, hard: 0, good: 0, easy: 0 };
  const [deckStats, setDeckStats] = useState<Record<string, DeckStatType>>({
    all: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } },
    words_phrases: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } },
    verbs: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } },
    connectors: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } },
    prepositions: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } },
    writing_errors: { total: 0, dueToday: 0, newToday: 0, reviewedToday: 0, byRating: { ...defaultByRating } }
  });

  const fetchCardsAndStats = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch ALL cards from database once
      const allCards = await flashcardService.getFlashcards({ studyType: "all" });

      // 2. Subdivide cards into categories
      const wordsCards = allCards.filter(c => {
        if (c.type === "verb" || c.type === "connector" || c.type === "preposition") return false;
        if (c.metadata?.staticDeck) return false;
        if (isWritingV2Flashcard(c)) return false;
        return true;
      });
      const verbsCards = allCards.filter(c => c.type === "verb" || c.metadata?.staticDeck === "verbs");
      const connectorsCards = allCards.filter(c => c.type === "connector" || c.metadata?.staticDeck === "connectors");
      const prepositionsCards = allCards.filter(c => c.type === "preposition" || c.metadata?.staticDeck === "prepositions");
      const writingCards = allCards.filter(isWritingV2Flashcard);

      // 3. Define calculation logic
      const now = new Date();
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const calculateStats = (dbCards: any[], staticLength?: number) => {
        let total = dbCards.length;
        let dueToday = 0;
        let newToday = 0;
        let reviewedToday = 0;
        const byRating = { again: 0, hard: 0, good: 0, easy: 0 };

        dbCards.forEach(card => {
          const nextReview = card.nextReviewAt ? new Date(card.nextReviewAt) : null;
          const isDue = !nextReview || nextReview <= now;
          const isNew = !card.reviewCount || card.reviewCount === 0;

          if (isDue) dueToday++;
          if (isNew) newToday++;

          const lastReviewed = card.lastReviewedAt ? new Date(card.lastReviewedAt) : null;
          if (lastReviewed && lastReviewed >= todayStart) {
            reviewedToday++;
          }
          
          if (card.metadata?.reviewHistory && Array.isArray(card.metadata.reviewHistory) && card.metadata.reviewHistory.length > 0) {
            const lastReview = card.metadata.reviewHistory[card.metadata.reviewHistory.length - 1];
            if (lastReview?.rating === "again") byRating.again++;
            else if (lastReview?.rating === "hard") byRating.hard++;
            else if (lastReview?.rating === "good") byRating.good++;
            else if (lastReview?.rating === "easy") byRating.easy++;
          }
        });

        if (staticLength !== undefined && staticLength > dbCards.length) {
          const missingCount = staticLength - dbCards.length;
          total += missingCount;
          dueToday += missingCount;
          newToday += missingCount;
        }

        return { total, dueToday, newToday, reviewedToday, byRating };
      };

      // 4. Compute stats for all 6 decks
      const wordsStats = calculateStats(wordsCards);
      const verbsStats = calculateStats(verbsCards);
      const connectorsStats = calculateStats(connectorsCards);
      const prepositionsStats = calculateStats(prepositionsCards);
      const writingStats = calculateStats(writingCards);

      const allStats = {
        total: wordsStats.total + verbsStats.total + connectorsStats.total + prepositionsStats.total + writingStats.total,
        dueToday: wordsStats.dueToday + verbsStats.dueToday + connectorsStats.dueToday + prepositionsStats.dueToday + writingStats.dueToday,
        newToday: wordsStats.newToday + verbsStats.newToday + connectorsStats.newToday + prepositionsStats.newToday + writingStats.newToday,
        reviewedToday: wordsStats.reviewedToday + verbsStats.reviewedToday + connectorsStats.reviewedToday + prepositionsStats.reviewedToday + writingStats.reviewedToday,
        byRating: {
          again: wordsStats.byRating.again + verbsStats.byRating.again + connectorsStats.byRating.again + prepositionsStats.byRating.again + writingStats.byRating.again,
          hard: wordsStats.byRating.hard + verbsStats.byRating.hard + connectorsStats.byRating.hard + prepositionsStats.byRating.hard + writingStats.byRating.hard,
          good: wordsStats.byRating.good + verbsStats.byRating.good + connectorsStats.byRating.good + prepositionsStats.byRating.good + writingStats.byRating.good,
          easy: wordsStats.byRating.easy + verbsStats.byRating.easy + connectorsStats.byRating.easy + prepositionsStats.byRating.easy + writingStats.byRating.easy,
        }
      };

      const computedStats = {
        all: allStats,
        words_phrases: wordsStats,
        verbs: verbsStats,
        connectors: connectorsStats,
        prepositions: prepositionsStats,
        writing_errors: writingStats
      };

      // Log temporary stats as required by Task 8
      console.log("[FLASHCARD_DECK_STATS]", computedStats);

      setDeckStats(computedStats);

      // 5. Filter dynamic active cards list for UI
      let activeCards: any[] = [];
      if (selectedStudyType === "all") {
        activeCards = allCards;
      } else if (selectedStudyType === "words") {
        activeCards = wordsCards;
      } else if (selectedStudyType === "verbs") {
        activeCards = verbsCards;
      } else if (selectedStudyType === "connectors") {
        activeCards = connectorsCards;
      } else if (selectedStudyType === "prepositions") {
        activeCards = prepositionsCards;
      } else if (selectedStudyType === "writing_v2") {
        activeCards = writingCards;
      }
      setCards(activeCards);

      // 6. Set bottom stats summary based on selectedStudyType
      const activeKey = 
        selectedStudyType === 'words' ? 'words_phrases' :
        selectedStudyType === 'writing_v2' ? 'writing_errors' :
        selectedStudyType;

      const activeStats = computedStats[activeKey];
      setStats({
        total: activeStats.total,
        dueToday: activeStats.dueToday,
        newCards: activeStats.newToday,
        reviewed: activeStats.reviewedToday,
        byRating: activeStats.byRating
      });

      // EE V2 logs
      if (writingCards.length > 0) {
        console.log("[FLASHCARDS_EE_V2_FOUND]", writingCards.map(card => ({
          id: card.id,
          section: card.metadata?.section,
          category: card.category || card.metadata?.category,
          difficulty: card.difficulty,
          hasExplanation: Boolean(card.back?.includes("Explicação") || card.metadata?.explanation),
          hasContext: Boolean(card.back?.includes("Contexto") || card.metadata?.contextSentence),
        })));

        console.log("[FLASHCARDS_EE_V2_DIFFICULTY_CHECK]", writingCards.map(card => ({
          id: card.id,
          difficulty: card.difficulty,
          severity: card.metadata?.severity,
          category: card.category,
        })));
      }

      console.log("[FlashcardList] difficulties:", activeCards.map(c => ({
        id: c.id,
        front: c.front,
        difficulty: c.difficulty
      })));

    } catch (err) {
      console.error("Error loading flashcards and stats:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCardsAndStats();
  }, [selectedStudyType]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchCardsAndStats();
    };
    window.addEventListener("flashcardsUpdated", handleUpdate);
    window.addEventListener("dashboardUpdated", handleUpdate);
    return () => {
      window.removeEventListener("flashcardsUpdated", handleUpdate);
      window.removeEventListener("dashboardUpdated", handleUpdate);
    };
  }, [selectedStudyType]);

  // Função de navegação com materialização prévia de SRS
  const handleStudyNavigation = async (mode: 'due' | 'all', targetStudyType: 'all' | 'words' | 'verbs' | 'connectors' | 'prepositions' | 'writing_v2') => {
    navigate(`/flashcards/study?mode=${mode}&type=${targetStudyType}`);
  };

  // Filter state for Personal Cards (words/phrases)
  const [filterDifficulty, setFilterDifficulty] = useState<string>(selectedDiff || "all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterOrigin, setFilterOrigin] = useState<string>("all");
  const [filterSection, setFilterSection] = useState<string>("all");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isMigratingData, setIsMigratingData] = useState(false);

  const handleMigrateNative = async () => {
    setIsMigratingData(true);
    try {
      const count = await flashcardService.migrateStaticDecksToDatabase();
      alert(`Migração concluída! ${count} decks foram garantidos no seu banco de dados.`);
      await fetchCardsAndStats();
    } catch (err: any) {
      alert("Erro na migração: " + err.message);
    } finally {
      setIsMigratingData(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Tem certeza que deseja EXCLUIR TODOS OS FLASHCARDS? Esta ação irá remover todos os seus flashcards do banco de dados para que você possa começar do zero.")) return;

    setIsDeletingAll(true);
    try {
      await flashcardService.deleteAllFlashcards();
      setSelectedIds([]);
      await fetchCardsAndStats();
      alert("Todos os flashcards foram excluídos com sucesso!");
    } catch (err: any) {
      console.error("Erro ao excluir todos os flashcards:", err);
      alert("Erro ao excluir flashcards: " + (err.message || ""));
    } finally {
      setIsDeletingAll(false);
    }
  };

  // State to control visible backs
  const [visibleBacks, setVisibleBacks] = useState<Record<string, boolean>>({});
  const toggleBackVisibility = (cardId: string) => {
    setVisibleBacks(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  // Filtros de Verbos
  const [verbSearch, setVerbSearch] = useState("");
  const [verbTenseFilter, setVerbTenseFilter] = useState("all");
  const [verbCatFilter, setVerbCatFilter] = useState("all");

  // Filtros de Conectores
  const [connectorSearch, setConnectorSearch] = useState("");
  const [connectorCatFilter, setConnectorCatFilter] = useState("all");

  // Filtros de Preposições
  const [prepositionSearch, setPrepositionSearch] = useState("");
  const [prepositionCatFilter, setPrepositionCatFilter] = useState("all");

  // Controle de visibilidade dos versos dos decks estáticos
  const [staticVisibleBacks, setStaticVisibleBacks] = useState<Record<string, boolean>>({});
  const toggleStaticBack = (id: string) => {
    setStaticVisibleBacks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Create card state
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Creation fields
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [category, setCategory] = useState("Grammaire");
  const [language, setLanguage] = useState("French");
  const [typeField, setTypeField] = useState("grammar");

  // Edit fields
  const [editFront, setEditFront] = useState("");
  const [editBack, setEditBack] = useState("");
  const [editDifficulty, setEditDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [editCategory, setEditCategory] = useState("");
  const [editLanguage, setEditLanguage] = useState("French");
  const [editType, setEditType] = useState("grammar");

  const handleOpenCreateModal = async () => {
    const isLimitedPlan = !profile?.plan || ["free", "trial", "expired"].includes(profile.plan);
    if (isLimitedPlan) {
      try {
        const allCustom = await flashcardService.getFlashcards({ studyType: "words" });
        if (allCustom.length >= 50) {
          alert(t("flashcards.limitReached", "Limite de 50 flashcards atingido no plano gratuito. Faça o upgrade para o Premium!"));
          navigate("/premium");
          return;
        }
      } catch (err) {
        console.error("Error checking flashcard limit:", err);
      }
    }

    setFront("");
    setBack("");
    setDifficulty("medium");
    setCategory("Grammaire");
    setLanguage("French");
    setTypeField("grammar");
    setIsModalOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    if (!front || !front.trim()) {
      alert(t("flashcards.alertRequiredFront"));
      return;
    }
    if (!back || !back.trim()) {
      alert(t("flashcards.alertRequiredBack"));
      return;
    }

    const isLimitedPlan = !profile?.plan || ["free", "trial", "expired"].includes(profile.plan);
    if (isLimitedPlan) {
      try {
        const allCustom = await flashcardService.getFlashcards({ studyType: "words" });
        if (allCustom.length >= 50) {
          alert(t("flashcards.limitReached", "Limite de 50 flashcards atingido no plano gratuito. Faça o upgrade para o Premium!"));
          navigate("/premium");
          setIsModalOpen(false);
          return;
        }
      } catch (err) {
        console.error("Error checking flashcard limit:", err);
      }
    }

    setIsSaving(true);
    console.log("[FlashcardForm] difficulty selected before submit:", difficulty);
    try {
      await flashcardService.createFlashcard({
        front,
        back,
        difficulty,
        category,
        language,
        type: typeField
      } as any);

      await fetchCardsAndStats();
      console.log("[FlashcardCreate] successfully created flashcard with difficulty:", difficulty);
      setFront("");
      setBack("");
      setIsModalOpen(false);
    } catch (err: any) {
      console.error("[Flashcards] failed to create flashcard", err);
      if (err.message === "TRIAL_LIMIT_EXCEEDED: flashcards" || err.message?.includes("TRIAL_LIMIT_EXCEEDED")) {
        alert(t("flashcards.limitReached", "Limite de 50 flashcards atingido no plano gratuito. Faça o upgrade para o Premium!"));
        navigate("/premium");
        setIsModalOpen(false);
      } else if (err.message && (err.message.includes("já existe") || err.message.includes("obrigatória") || err.message.includes("obrigatório"))) {
        alert(err.message);
      } else if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        alert(t("flashcards.alertExpiredSession"));
      } else {
        alert(t("flashcards.alertGeneralError"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const startEditCard = (card: any) => {
    setEditFront(card.front || "");
    setEditBack(card.back || "");
    setEditDifficulty(normalizeFlashcardDifficulty(card.difficulty));
    setEditCategory(card.category || "Grammaire");
    setEditLanguage(card.language || "French");
    setEditType(card.type || "grammar");
    setIsEditing(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCard) return;
    if (isSaving) return;

    if (!editFront || !editFront.trim()) {
      alert(t("flashcards.alertRequiredFront"));
      return;
    }
    if (!editBack || !editBack.trim()) {
      alert(t("flashcards.alertRequiredBack"));
      return;
    }

    setIsSaving(true);
    try {
      await flashcardService.updateFlashcard(selectedCard.id, {
        front: editFront,
        back: editBack,
        difficulty: editDifficulty,
        category: editCategory,
        language: editLanguage,
        type: editType
      });

      await fetchCardsAndStats();
      setSelectedCard(null);
      setIsEditing(false);
    } catch (err: any) {
      console.error("[Flashcards] failed to update flashcard", err);
      if (err.message && (err.message.includes("já existe") || err.message.includes("obrigatória") || err.message.includes("obrigatório"))) {
        alert(err.message);
      } else if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        alert(t("flashcards.alertSaveExpired"));
      } else {
        alert(t("flashcards.alertSaveGeneral"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("flashcards.confirmDeleteFlashcard"))) return;
    try {
      await flashcardService.deleteFlashcard(id);
      fetchCardsAndStats();
      if (selectedCard?.id === id) {
        setSelectedCard(null);
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error("Error deleting flashcard:", err);
      let errMsg = err.message || t("common.error");
      if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        errMsg = t("flashcards.alertDeleteExpired");
      }
      alert(errMsg);
    }
  };

  const renderEEV2Back = (backText: string) => {
    if (!backText) return null;
    const hasMarkers = backText.includes("Correção:") || backText.includes("Explicação:") || backText.includes("Contexto:");
    if (!hasMarkers) {
      return <div className="whitespace-pre-line text-xs font-semibold text-gray-700 leading-relaxed">{backText}</div>;
    }
    const blocks: { type: string; title: string; content: string }[] = [];
    const lines = backText.split("\n");
    let currentBlock: { type: string; title: string; content: string[] } | null = null;
    lines.forEach(line => {
      const cleanLine = line.trim();
      if (cleanLine.startsWith("Correção:")) {
        if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
        currentBlock = { type: "correction", title: "Sentença Corrigida", content: [cleanLine.replace("Correção:", "").trim()] };
      } else if (cleanLine.startsWith("Explicação:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { type: "explanation", title: "Explicação do Erro", content: [cleanLine.replace("Explicação:", "").trim()] };
      } else if (cleanLine.startsWith("Contexto:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { type: "context", title: "Frase de Contexto", content: [cleanLine.replace("Contexto:", "").trim()] };
      } else if (cleanLine.startsWith("Categoria:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { type: "category", title: "Categoria", content: [cleanLine.replace("Categoria:", "").trim()] };
      } else {
         if (currentBlock) {
           currentBlock.content.push(line);
         } else if (cleanLine) {
           currentBlock = { type: "general", title: "Informação", content: [line] };
         }
      }
    });
    if (currentBlock) {
      blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
    }
    return (
      <div className="space-y-2.5 text-left w-full pt-2">
        {blocks.map((block, idx) => {
          if (!block.content) return null;
          if (block.type === "correction") {
            return (
              <div key={idx} className="space-y-0.5 bg-green-50 border border-green-100 p-2.5 rounded-xl">
                <span className="text-[9px] text-green-700 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-xs font-bold text-emerald-950">{block.content}</p>
              </div>
            );
          }
          if (block.type === "explanation") {
            return (
              <div key={idx} className="space-y-0.5 bg-slate-50 border border-slate-100 p-2.5 rounded-xl">
                <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-[11px] text-slate-700 leading-relaxed font-semibold">{block.content}</p>
              </div>
            );
          }
          if (block.type === "context") {
            return (
              <div key={idx} className="space-y-0.5 bg-gray-50/50 border border-gray-100/50 p-2.5 rounded-xl">
                <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-[11px] text-slate-600 italic">"{block.content}"</p>
              </div>
            );
          }
          return (
            <div key={idx} className="space-y-0.5 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
              <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
              <p className="text-[11px] text-slate-700">{block.content}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const getFilteredCards = () => {
    const filtered = cards.filter((card: any) => {
      if (filterDifficulty !== "all") {
        const cardDiff = card.difficulty || "medium";
        if (filterDifficulty === "hard") {
          if (cardDiff !== "hard" && cardDiff !== "difficult" && cardDiff !== "difícil") return false;
        } else if (filterDifficulty === "easy") {
          if (cardDiff !== "easy" && cardDiff !== "facil" && cardDiff !== "fácil") return false;
        } else {
          if (cardDiff !== filterDifficulty) return false;
        }
      }
      if (filterCategory !== "all") {
        const cat = (card.category || card.metadata?.category || "").toLowerCase();
        const isMatch = cat === filterCategory.toLowerCase() || 
          (filterCategory === "Gramática" && cat === "grammaire") ||
          (filterCategory === "Ortografia" && cat === "orthographe") ||
          (filterCategory === "Concordância" && cat === "accord") ||
          (filterCategory === "Conjugação" && cat === "conjugaison") ||
          (filterCategory === "Preposição" && (cat === "préposition" || cat === "preposição")) ||
          (filterCategory === "Pontuação" && cat === "ponctuation");
        
        if (!isMatch && cat !== filterCategory.toLowerCase()) return false;
      }
      if (filterOrigin !== "all") {
        const isV2 = isWritingV2Flashcard(card);
        const isAi = card.source === "writing_ai_correction";
        if (filterOrigin === "writing_v2" && !isV2) return false;
        if (filterOrigin === "ai" && !isAi) return false;
        if (filterOrigin === "manual" && (isV2 || isAi)) return false;
      }
      if (filterSection !== "all") {
        const section = card.metadata?.section || "";
        if (filterSection === "Section A") {
          if (!section.includes("A")) return false;
        } else if (filterSection === "Section B") {
          if (!section.includes("B")) return false;
        }
      }
      return true;
    });

    if (selectedStudyType === "writing_v2" || filterOrigin === "writing_v2") {
      console.log("[FLASHCARDS_EE_V2_FILTER_APPLIED]", {
        count: filtered.length,
      });
    }

    return filtered;
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t("flashcards.confirmDeleteFlashcards", { count: selectedIds.length }))) return;

    setIsDeletingBulk(true);
    console.log("[FLASHCARDS_BULK_DELETE_START]", {
      count: selectedIds.length,
      selectedIds,
    });

    try {
      const deletedCount = await flashcardService.deleteMultipleFlashcards(selectedIds);
      console.log("[FLASHCARDS_BULK_DELETE_SUCCESS]", {
        count: deletedCount,
      });
      setSelectedIds([]);
      await fetchCardsAndStats();
    } catch (err: any) {
      console.error("[FLASHCARDS_BULK_DELETE_ERROR]", err);
      alert(t("common.error") + ": " + (err.message || ""));
    } finally {
      setIsDeletingBulk(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-16">
      {/* HEADER & VOLTAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 mb-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </Button>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <span>{t("flashcards.title")}</span>
            <Badge className="bg-primary/10 text-primary border-none text-xs font-bold px-2.5 py-0.5">{t("flashcards.badgeSrsGlobal", "SRS Global")}</Badge>
          </h1>
          <p className="text-gray-500 mt-1 text-sm font-medium">{t("flashcards.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2.5 shrink-0 items-center">
          <Button 
            onClick={handleMigrateNative}
            disabled={isMigratingData}
            variant="outline"
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl shadow-xs font-bold text-xs px-3.5 py-2"
            title="Importar decks nativos de verbos, conectores e preposições"
          >
            {isMigratingData ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin text-purple-600" /> : <RefreshCw className="w-4 h-4 mr-1.5 text-purple-600" />}
            Restaurar Decks Nativos
          </Button>
          <Button 
            onClick={handleDeleteAll}
            disabled={isDeletingAll}
            variant="outline"
            className="bg-white border border-red-200 hover:bg-red-50 text-red-600 rounded-xl shadow-xs font-bold text-xs px-3.5 py-2"
            title="Excluir todos os flashcards para começar do zero"
          >
            {isDeletingAll ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            Excluir Todos os Flashcards
          </Button>
          <Button 
            onClick={handleOpenCreateModal} 
            className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-xs font-bold text-xs px-4 py-2"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            {t("flashcards.createFlashcardButton")}
          </Button>
        </div>
      </div>

      {isImporting && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-center gap-3 text-blue-700 font-bold text-xs animate-pulse">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>{t("flashcards.importingMsg")}</span>
        </div>
      )}

      {/* SEÇÃO PRINCIPAL: ESCOLHA O QUE DESEJA TREINAR (5 CARDS) */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-gray-900 tracking-tight">{t("flashcards.studyTypeHeading")}</h2>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 min-[1750px]:grid-cols-6">
          {/* CARD 1: TREINAR TODOS (CINZA/ROXO NEUTRO) */}
          <div 
            onClick={() => setSelectedStudyType('all')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'all' 
                ? 'bg-slate-900 border-slate-700 shadow-xl shadow-slate-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-slate-400 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-slate-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'all' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                  <Layers className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'all' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'all' ? 'bg-slate-800 text-white border-none' : 'bg-slate-100 text-slate-800 border-none'}`}>
                    {t("flashcards.badgeSrsDecks")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'all' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.allCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'all' ? 'text-slate-200' : 'text-gray-500'}`}>
                  {t("flashcards.allCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-slate-500/20 pt-4">
                <span className={selectedStudyType === 'all' ? 'text-slate-300' : 'text-gray-400'}>{t("flashcards.deckTotalCards")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'all' ? 'text-white' : 'text-slate-700'}`}>{deckStats.all.total}</span>
              </div>
              {selectedStudyType === 'all' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'all'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-slate-700 hover:bg-slate-600 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'all'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'all' ? 'border-slate-700 bg-slate-800 text-white hover:bg-slate-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* CARD 2: PALAVRAS OU FRASES (ROXO) */}
          <div 
            onClick={() => setSelectedStudyType('words')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'words' 
                ? 'bg-purple-950 border-purple-700 shadow-xl shadow-purple-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-purple-300 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'words' ? 'bg-purple-800 text-purple-200' : 'bg-purple-50 text-purple-700'}`}>
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'words' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'words' ? 'bg-purple-800 text-white border-none' : 'bg-purple-100 text-purple-800 border-none'}`}>
                    {t("flashcards.badgeSrsPersonal")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'words' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.wordsCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'words' ? 'text-purple-200' : 'text-gray-500'}`}>
                  {t("flashcards.wordsCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-purple-500/20 pt-4">
                <span className={selectedStudyType === 'words' ? 'text-purple-200' : 'text-gray-400'}>{t("flashcards.deckCardsCount")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'words' ? 'text-white' : 'text-purple-700'}`}>{deckStats.words_phrases.total}</span>
              </div>
              {selectedStudyType === 'words' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'words'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-purple-600 hover:bg-purple-500 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'words'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'words' ? 'border-purple-700 bg-purple-800 text-white hover:bg-purple-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* CARD 3: VERBOS (AZUL) */}
          <div 
            onClick={() => setSelectedStudyType('verbs')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'verbs' 
                ? 'bg-blue-900 border-blue-700 shadow-xl shadow-blue-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-blue-300 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'verbs' ? 'bg-blue-800 text-blue-200' : 'bg-blue-50 text-blue-700'}`}>
                  <Zap className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'verbs' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'verbs' ? 'bg-blue-800 text-white border-none' : 'bg-blue-100 text-blue-800 border-none'}`}>
                    {t("flashcards.badgeSrsFree")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'verbs' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.verbsCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'verbs' ? 'text-blue-200' : 'text-gray-500'}`}>
                  {t("flashcards.verbsCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-blue-500/20 pt-4">
                <span className={selectedStudyType === 'verbs' ? 'text-blue-200' : 'text-gray-400'}>{t("flashcards.deckVerbsCount")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'verbs' ? 'text-white' : 'text-blue-700'}`}>{deckStats.verbs.total} cards</span>
              </div>
              {selectedStudyType === 'verbs' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'verbs'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'verbs'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'verbs' ? 'border-blue-700 bg-blue-800 text-white hover:bg-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* CARD 4: CONECTORES (VERDE) */}
          <div 
            onClick={() => setSelectedStudyType('connectors')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'connectors' 
                ? 'bg-emerald-900 border-emerald-700 shadow-xl shadow-emerald-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-emerald-300 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'connectors' ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-50 text-emerald-700'}`}>
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'connectors' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'connectors' ? 'bg-emerald-800 text-white border-none' : 'bg-emerald-100 text-emerald-800 border-none'}`}>
                    {t("flashcards.badgeSrsFree")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'connectors' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.connectorsCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'connectors' ? 'text-emerald-200' : 'text-gray-500'}`}>
                  {t("flashcards.connectorsCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-emerald-500/20 pt-4">
                <span className={selectedStudyType === 'connectors' ? 'text-emerald-200' : 'text-gray-400'}>{t("flashcards.deckConnectorsCount")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'connectors' ? 'text-white' : 'text-emerald-700'}`}>{deckStats.connectors.total} cards</span>
              </div>
              {selectedStudyType === 'connectors' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'connectors'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'connectors'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'connectors' ? 'border-emerald-700 bg-emerald-800 text-white hover:bg-emerald-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* CARD 5: PREPOSIÇÕES (LARANJA) */}
          <div 
            onClick={() => setSelectedStudyType('prepositions')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'prepositions' 
                ? 'bg-amber-900 border-amber-700 shadow-xl shadow-amber-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-amber-300 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'prepositions' ? 'bg-amber-800 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>
                  <Compass className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'prepositions' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'prepositions' ? 'bg-amber-800 text-white border-none' : 'bg-amber-100 text-amber-800 border-none'}`}>
                    {t("flashcards.badgeSrsFree")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'prepositions' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.prepositionsCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'prepositions' ? 'text-amber-200' : 'text-gray-500'}`}>
                  {t("flashcards.prepositionsCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-amber-500/20 pt-4">
                <span className={selectedStudyType === 'prepositions' ? 'text-amber-200' : 'text-gray-400'}>{t("flashcards.deckPrepositionsCount")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'prepositions' ? 'text-white' : 'text-amber-700'}`}>{deckStats.prepositions.total} cards</span>
              </div>
              {selectedStudyType === 'prepositions' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'prepositions'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-amber-600 hover:bg-amber-500 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'prepositions'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'prepositions' ? 'border-amber-700 bg-amber-800 text-white hover:bg-amber-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* CARD 6: EXPRESSION ÉCRITE (INDIGO/ROXO ESCURO) */}
          <div 
            onClick={() => setSelectedStudyType('writing_v2')}
            className={`p-5 min-h-[210px] rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden ${
              selectedStudyType === 'writing_v2' 
                ? 'bg-indigo-900 border-indigo-700 shadow-xl shadow-indigo-900/20 text-white scale-[1.02]' 
                : 'bg-white border-gray-200/80 hover:border-indigo-300 hover:shadow-md text-gray-800'
            }`}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-bl-full pointer-events-none" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${selectedStudyType === 'writing_v2' ? 'bg-indigo-800 text-indigo-200' : 'bg-indigo-50 text-indigo-700'}`}>
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {selectedStudyType === 'writing_v2' && (
                    <Badge className="bg-white/20 text-white border-none text-[10px] font-black uppercase tracking-wider whitespace-nowrap">{t("flashcards.selected")}</Badge>
                  )}
                  <Badge className={`text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${selectedStudyType === 'writing_v2' ? 'bg-indigo-800 text-white border-none' : 'bg-indigo-100 text-indigo-800 border-none'}`}>
                    {t("flashcards.badgeWritingErrors")}
                  </Badge>
                </div>
              </div>
              <div>
                <h3 className={`font-black text-sm min-[400px]:text-base tracking-tight line-clamp-2 leading-snug ${selectedStudyType === 'writing_v2' ? 'text-white' : 'text-gray-900'}`}>{t("flashcards.writingCardsTitle")}</h3>
                <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${selectedStudyType === 'writing_v2' ? 'text-indigo-200' : 'text-gray-500'}`}>
                  {t("flashcards.writingCardsDesc")}
                </p>
              </div>
            </div>

            <div className="pt-7 space-y-4">
              <div className="flex items-center justify-between text-xs font-bold border-t border-indigo-500/20 pt-4">
                <span className={selectedStudyType === 'writing_v2' ? 'text-indigo-200' : 'text-gray-400'}>{t("flashcards.deckCardsCount")}</span>
                <span className={`text-sm font-black ${selectedStudyType === 'writing_v2' ? 'text-white' : 'text-indigo-700'}`}>{deckStats.writing_errors.total}</span>
              </div>
              {selectedStudyType === 'writing_v2' && (
                <div className="grid w-full grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 min-[1750px]:grid-cols-1 gap-2">
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('due', 'writing_v2'); }}
                    className="min-h-[44px] h-auto w-full min-w-0 whitespace-normal text-center text-xs font-bold leading-snug bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl shadow-md px-3 py-2 flex items-center justify-center"
                  >
                    {t("flashcards.srsButton")}
                  </Button>
                  <Button 
                    disabled={isImporting}
                    onClick={(e) => { e.stopPropagation(); handleStudyNavigation('all', 'writing_v2'); }}
                    variant="outline" 
                    className={`min-h-[44px] h-auto rounded-xl text-xs font-bold px-3 py-2 w-full min-w-0 whitespace-normal text-center leading-snug flex items-center justify-center ${selectedStudyType === 'writing_v2' ? 'border-indigo-700 bg-indigo-800 text-white hover:bg-indigo-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t("flashcards.practiceButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ESTATÍSTICAS DINÂMICAS PADRONIZADAS (4 CAIXAS FIXAS) */}
      <div className="grid gap-4 md:grid-cols-4 pt-2">
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{t("flashcards.statsTotal")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-gray-800">{stats.total}</div>
            <p className="text-xs text-gray-500 mt-1 font-medium">{t("flashcards.statsMode")}</p>
          </CardContent>
        </Card>
        
        <Card className="border-none shadow-sm bg-primary text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-primary-foreground/80 font-black uppercase tracking-wider">{t("flashcards.statsDue")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-white">{stats.dueToday}</div>
            <p className="text-xs text-primary-foreground/80 mt-1 font-medium">{t("flashcards.statsDueDesc")}</p>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{t("flashcards.statsNew")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-gray-800">{stats.newCards}</div>
            <p className="text-xs text-amber-500 mt-1 font-medium">{t("flashcards.statsNewDesc")}</p>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{t("flashcards.statsReviewed")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-gray-800">{stats.reviewed}</div>
            <p className="text-xs text-green-500 mt-1 font-medium">{t("flashcards.statsReviewedDesc")}</p>
          </CardContent>
        </Card>
      </div>

      {/* ESTATÍSTICAS DE DESEMPENHO (STATUS ATUAL) */}
      <div className="grid gap-4 md:grid-cols-4 pt-2">
        <Card className="border border-red-100 shadow-sm bg-red-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-red-500 font-black uppercase tracking-wider">Errei / Refazer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-red-700">{stats.byRating?.again || 0}</div>
            <p className="text-[10px] text-red-600/70 mt-1 font-bold">Status atual</p>
          </CardContent>
        </Card>
        
        <Card className="border border-orange-100 shadow-sm bg-orange-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-orange-500 font-black uppercase tracking-wider">Difícil</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-orange-700">{stats.byRating?.hard || 0}</div>
            <p className="text-[10px] text-orange-600/70 mt-1 font-bold">Status atual</p>
          </CardContent>
        </Card>

        <Card className="border border-emerald-100 shadow-sm bg-emerald-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-emerald-600 font-black uppercase tracking-wider">Bom / Acertei</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-emerald-800">{stats.byRating?.good || 0}</div>
            <p className="text-[10px] text-emerald-700/70 mt-1 font-bold">Status atual</p>
          </CardContent>
        </Card>

        <Card className="border border-indigo-100 shadow-sm bg-indigo-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-[10px] text-indigo-500 font-black uppercase tracking-wider">Fácil / Dominado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-indigo-700">{stats.byRating?.easy || 0}</div>
            <p className="text-[10px] text-indigo-600/70 mt-1 font-bold">Status atual</p>
          </CardContent>
        </Card>
      </div>

      {/* ÁREA DE LISTAGEM SEGMENTADA ESTUDO ATUAL */}
      <div className="pt-4 space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900 tracking-tight">
              {selectedStudyType === 'all' ? t("flashcards.listAllTitle") :
               selectedStudyType === 'words' ? t("flashcards.listWordsTitle") :
               selectedStudyType === 'verbs' ? t("flashcards.listVerbsTitle") :
               selectedStudyType === 'connectors' ? t("flashcards.listConnectorsTitle") :
               selectedStudyType === 'prepositions' ? t("flashcards.listPrepositionsTitle") :
               t("flashcards.listWritingTitle")}
            </h3>
            {(selectedStudyType === 'verbs' || selectedStudyType === 'connectors' || selectedStudyType === 'prepositions') && (
              <Button 
                onClick={handleMigrateNative} 
                disabled={isMigratingData}
                variant="outline" 
                size="sm"
                className="h-7 text-[10px] font-bold border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100"
              >
                {isMigratingData ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Migrar para Nuvem
              </Button>
            )}
          </div>
          <span className="text-xs font-semibold text-gray-500">
            {t("flashcards.cardsFoundMsg", { count: cards.length })}
          </span>
        </div>

        {isLoading ? (
          <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse">
            <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
            <h4 className="font-bold text-gray-700 text-base">{t("flashcardStudy.loading")}</h4>
            <p className="text-xs text-gray-500 mt-1">{t("writingErrors.loadingDesc")}</p>
          </div>
        ) : selectedStudyType === 'verbs' ? (
          /* LISTAGEM DE VERBOS */
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder={t("flashcards.searchVerb")}
                  value={verbSearch}
                  onChange={e => setVerbSearch(e.target.value)}
                  className="pl-9 h-9 text-xs rounded-xl border-gray-200"
                />
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="flex flex-col gap-1 w-full md:w-40">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.verbTense")}</label>
                  <select
                    value={verbTenseFilter}
                    onChange={e => setVerbTenseFilter(e.target.value)}
                    className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-1.5 border border-gray-100 cursor-pointer focus:outline-none"
                  >
                    <option value="all">{t("flashcards.allTenses")}</option>
                    <option value="Présent">{t("flashcards.tenses.present", "Présent")}</option>
                    <option value="Passé composé">{t("flashcards.tenses.passeCompose", "Passé composé")}</option>
                    <option value="Imparfait">{t("flashcards.tenses.imparfait", "Imparfait")}</option>
                    <option value="Futur simple">{t("flashcards.tenses.futurSimple", "Futur simple")}</option>
                    <option value="Conditionnel présent">{t("flashcards.tenses.conditionnelPresent", "Conditionnel présent")}</option>
                    <option value="Subjonctif présent">{t("flashcards.tenses.subjonctifPresent", "Subjonctif présent")}</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1 w-full md:w-40">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.category")}</label>
                  <select
                    value={verbCatFilter}
                    onChange={e => setVerbCatFilter(e.target.value)}
                    className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-1.5 border border-gray-100 cursor-pointer focus:outline-none"
                  >
                    <option value="all">{t("flashcards.allCategories")}</option>
                    {Array.from(new Set(cards.map(c => c.category))).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(() => {
                const filtered = cards.filter(c => {
                  const targetTense = c.tense || c.metadata?.tense;
                  const targetVerb = c.verb || c.metadata?.verb || "";
                  const targetExample = c.example || c.metadata?.example || "";

                  if (verbTenseFilter !== "all" && targetTense !== verbTenseFilter) return false;
                  if (verbCatFilter !== "all" && c.category !== verbCatFilter) return false;
                  if (verbSearch.trim()) {
                    const query = verbSearch.toLowerCase();
                    const matchesVerb = targetVerb.toLowerCase().includes(query);
                    const matchesExample = targetExample.toLowerCase().includes(query);
                    if (!matchesVerb && !matchesExample) return false;
                  }
                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="col-span-full py-12 text-center text-gray-400 font-medium text-xs bg-gray-50/30 rounded-2xl border border-dashed border-gray-200">
                      {t("flashcards.noVerbFoundMsg", { defaultValue: "Nenhum card de verbo encontrado com a busca atual." })}
                    </div>
                  );
                }

                return (
                  <div className="col-span-full space-y-4">
                    <div className="bg-purple-50/50 border border-purple-100 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox"
                          checked={selectedIds.length > 0 && filtered.every(c => selectedIds.includes(c.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newIds = [...selectedIds];
                              filtered.forEach(c => {
                                if (!newIds.includes(c.id)) newIds.push(c.id);
                              });
                              setSelectedIds(newIds);
                            } else {
                              const visibleIds = filtered.map(c => c.id);
                              setSelectedIds(selectedIds.filter(id => !visibleIds.includes(id)));
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <span className="text-xs font-bold text-gray-700">
                          {selectedIds.length > 0 
                            ? t("flashcards.selectedCountMsg", { count: selectedIds.length, total: filtered.length }) 
                            : t("flashcards.selectAllCountMsg", { count: filtered.length })
                          }
                        </span>
                      </div>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isDeletingBulk}
                          onClick={handleBulkDelete}
                          className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
                        >
                          {isDeletingBulk ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-2" />}
                          {t("flashcards.deleteSelected", { count: selectedIds.length })}
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.map(c => {
                        const targetTense = c.tense || c.metadata?.tense || "Présent";
                        const targetVerb = c.verb || c.metadata?.verb || c.front;
                        const targetExample = c.example || c.metadata?.example || "";
                        const targetBack = c.back || "";
                        const isChecked = selectedIds.includes(c.id);

                        return (
                          <Card 
                            key={c.id} 
                            onClick={() => setSelectedCard(c)}
                            className={`border-none shadow-sm bg-white flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 ${
                              isChecked ? 'ring-2 ring-purple-500 bg-purple-50/10' : ''
                            }`}
                          >
                            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4">
                              <div className="flex items-start gap-3 flex-1">
                                <input 
                                  type="checkbox"
                                  checked={isChecked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedIds(prev => [...prev, c.id]);
                                    else setSelectedIds(prev => prev.filter(id => id !== c.id));
                                  }}
                                  className="w-4 h-4 mt-1 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer shrink-0"
                                />
                                <div className="space-y-1.5 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <Badge className="bg-blue-100 text-blue-800 border-none font-bold text-[9px]">{targetTense}</Badge>
                                    <Badge variant="outline" className="text-[8px] text-gray-500 bg-gray-50 font-semibold border-gray-100">{c.category}</Badge>
                                  </div>
                                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block pt-1">{t("flashcards.verbVerb")} <span className="text-gray-800 font-black">{targetVerb}</span></span>
                                  <h4 className="font-bold text-gray-900 text-xs leading-relaxed">{c.front}</h4>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(c.id);
                                }}
                                className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="pb-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t("flashcards.verbConjugation")}</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleStaticBack(c.id); }}
                                  className="text-blue-600 hover:text-blue-800 font-bold text-[10px] p-1 rounded hover:bg-blue-50 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  {staticVisibleBacks[c.id] ? <><EyeOff className="w-3.5 h-3.5" />{t("flashcards.hideBack")}</> : <><Eye className="w-3.5 h-3.5" />{t("flashcards.viewBack")}</>}
                                </button>
                              </div>

                              {staticVisibleBacks[c.id] ? (
                                <pre className="text-xs text-blue-950 font-mono bg-blue-50/50 p-3 rounded-xl border border-blue-100 whitespace-pre-wrap leading-relaxed animate-in fade-in duration-200">
                                  {targetBack}
                                </pre>
                              ) : (
                                <div className="text-xs text-gray-400 font-medium italic bg-gray-50/80 p-3 rounded-xl border border-gray-100 flex items-center justify-center gap-2 animate-in fade-in duration-200">
                                  <EyeOff className="w-3.5 h-3.5 opacity-50" />
                                  <span>{t("flashcards.verbContext")}</span>
                                </div>
                              )}

                              {targetExample && (
                                <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100 text-[11px]">
                                  <span className="font-bold text-gray-400 block text-[9px] uppercase">{t("flashcards.verbExample")}</span>
                                  <p className="text-gray-700 italic font-medium mt-0.5">{targetExample}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : selectedStudyType === 'connectors' ? (
          /* LISTAGEM DE CONECTORES */
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder={t("flashcards.searchConnector")}
                  value={connectorSearch}
                  onChange={e => setConnectorSearch(e.target.value)}
                  className="pl-9 h-9 text-xs rounded-xl border-gray-200"
                />
              </div>
              <div className="flex flex-col gap-1 w-full md:w-48">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.category")}</label>
                <select
                  value={connectorCatFilter}
                  onChange={e => setConnectorCatFilter(e.target.value)}
                  className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-1.5 border border-gray-100 cursor-pointer focus:outline-none"
                >
                  <option value="all">{t("flashcards.allCategories")}</option>
                  {Array.from(new Set(cards.map(c => c.category))).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(() => {
                const filtered = cards.filter(c => {
                  if (connectorCatFilter !== "all" && c.category !== connectorCatFilter) return false;
                  if (connectorSearch.trim()) {
                    const query = connectorSearch.toLowerCase();
                    const matchesFront = c.front.toLowerCase().includes(query);
                    const matchesBack = c.back.toLowerCase().includes(query);
                    if (!matchesFront && !matchesBack) return false;
                  }
                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="col-span-full py-12 text-center text-gray-400 font-medium text-xs bg-gray-50/30 rounded-2xl border border-dashed border-gray-200">
                      {t("flashcards.noConnectorFoundMsg", { defaultValue: "Nenhum conector encontrado com a busca atual." })}
                    </div>
                  );
                }

                return (
                  <div className="col-span-full space-y-4">
                    <div className="bg-purple-50/50 border border-purple-100 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox"
                          checked={selectedIds.length > 0 && filtered.every(c => selectedIds.includes(c.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newIds = [...selectedIds];
                              filtered.forEach(c => {
                                if (!newIds.includes(c.id)) newIds.push(c.id);
                              });
                              setSelectedIds(newIds);
                            } else {
                              const visibleIds = filtered.map(c => c.id);
                              setSelectedIds(selectedIds.filter(id => !visibleIds.includes(id)));
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <span className="text-xs font-bold text-gray-700">
                          {selectedIds.length > 0 
                            ? t("flashcards.selectedCountMsg", { count: selectedIds.length, total: filtered.length }) 
                            : t("flashcards.selectAllCountMsg", { count: filtered.length })
                          }
                        </span>
                      </div>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isDeletingBulk}
                          onClick={handleBulkDelete}
                          className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
                        >
                          {isDeletingBulk ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-2" />}
                          {t("flashcards.deleteSelected", { count: selectedIds.length })}
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.map(c => {
                        const targetExample = c.example || c.metadata?.example || "";
                        const isChecked = selectedIds.includes(c.id);
                        
                        return (
                          <Card 
                            key={c.id}
                            onClick={() => setSelectedCard(c)}
                            className={`border-none shadow-sm bg-white flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 ${
                              isChecked ? 'ring-2 ring-purple-500 bg-purple-50/10' : ''
                            }`}
                          >
                            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4">
                              <div className="flex items-start gap-3 flex-1">
                                <input 
                                  type="checkbox"
                                  checked={isChecked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedIds(prev => [...prev, c.id]);
                                    else setSelectedIds(prev => prev.filter(id => id !== c.id));
                                  }}
                                  className="w-4 h-4 mt-1 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer shrink-0"
                                />
                                <div className="space-y-1.5 flex-1">
                                  <Badge className="bg-emerald-100 text-emerald-800 border-none font-bold text-[9px] w-fit">{c.category}</Badge>
                                  <h4 className="font-bold text-gray-900 text-xs leading-relaxed pt-1">{c.front}</h4>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(c.id);
                                }}
                                className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="pb-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t("flashcards.connectorTranslation")}</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleStaticBack(c.id); }}
                                  className="text-emerald-600 hover:text-emerald-800 font-bold text-[10px] p-1 rounded hover:bg-emerald-50 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  {staticVisibleBacks[c.id] ? <><EyeOff className="w-3.5 h-3.5" />{t("flashcards.hideBack")}</> : <><Eye className="w-3.5 h-3.5" />{t("flashcards.viewBack")}</>}
                                </button>
                              </div>

                              {staticVisibleBacks[c.id] ? (
                                <p className="text-xs text-emerald-950 font-bold bg-emerald-50/50 p-3 rounded-xl border border-emerald-100 leading-relaxed animate-in fade-in duration-200">
                                  {c.back}
                                </p>
                              ) : (
                                <div className="text-xs text-gray-400 font-medium italic bg-gray-50/80 p-3 rounded-xl border border-gray-100 flex items-center justify-center gap-2 animate-in fade-in duration-200">
                                  <EyeOff className="w-3.5 h-3.5 opacity-50" />
                                  <span>{t("flashcards.verbContext")}</span>
                                </div>
                              )}

                              {targetExample && (
                                <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100 text-[11px]">
                                  <span className="font-bold text-gray-400 block text-[9px] uppercase">{t("flashcards.verbExample")}</span>
                                  <p className="text-gray-700 italic font-medium mt-0.5">{targetExample}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : selectedStudyType === 'prepositions' ? (
          /* LISTAGEM DE PREPOSIÇÕES */
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder={t("flashcards.searchPreposition")}
                  value={prepositionSearch}
                  onChange={e => setPrepositionSearch(e.target.value)}
                  className="pl-9 h-9 text-xs rounded-xl border-gray-200"
                />
              </div>
              <div className="flex flex-col gap-1 w-full md:w-48">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.category")}</label>
                <select
                  value={prepositionCatFilter}
                  onChange={e => setPrepositionCatFilter(e.target.value)}
                  className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-1.5 border border-gray-100 cursor-pointer focus:outline-none"
                >
                  <option value="all">{t("flashcards.allCategories")}</option>
                  {Array.from(new Set(cards.map(c => c.category))).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(() => {
                const filtered = cards.filter(c => {
                  const targetPrep = c.preposition || c.metadata?.preposition || "";
                  if (prepositionCatFilter !== "all" && c.category !== prepositionCatFilter) return false;
                  if (prepositionSearch.trim()) {
                    const query = prepositionSearch.toLowerCase();
                    const matchesPrep = targetPrep.toLowerCase().includes(query);
                    const matchesFront = c.front.toLowerCase().includes(query);
                    const matchesBack = c.back.toLowerCase().includes(query);
                    if (!matchesPrep && !matchesFront && !matchesBack) return false;
                  }
                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="col-span-full py-12 text-center text-gray-400 font-medium text-xs bg-gray-50/30 rounded-2xl border border-dashed border-gray-200">
                      {t("flashcards.noPrepositionFoundMsg", { defaultValue: "Nenhuma preposição encontrada com a busca atual." })}
                    </div>
                  );
                }

                return (
                  <div className="col-span-full space-y-4">
                    <div className="bg-purple-50/50 border border-purple-100 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox"
                          checked={selectedIds.length > 0 && filtered.every(c => selectedIds.includes(c.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newIds = [...selectedIds];
                              filtered.forEach(c => {
                                if (!newIds.includes(c.id)) newIds.push(c.id);
                              });
                              setSelectedIds(newIds);
                            } else {
                              const visibleIds = filtered.map(c => c.id);
                              setSelectedIds(selectedIds.filter(id => !visibleIds.includes(id)));
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <span className="text-xs font-bold text-gray-700">
                          {selectedIds.length > 0 
                            ? t("flashcards.selectedCountMsg", { count: selectedIds.length, total: filtered.length }) 
                            : t("flashcards.selectAllCountMsg", { count: filtered.length })
                          }
                        </span>
                      </div>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isDeletingBulk}
                          onClick={handleBulkDelete}
                          className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
                        >
                          {isDeletingBulk ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-2" />}
                          {t("flashcards.deleteSelected", { count: selectedIds.length })}
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.map(c => {
                        const targetPrep = c.preposition || c.metadata?.preposition || c.front;
                        const targetExample = c.example || c.metadata?.example || "";
                        const isChecked = selectedIds.includes(c.id);
                        
                        return (
                          <Card 
                            key={c.id}
                            onClick={() => setSelectedCard(c)}
                            className={`border-none shadow-sm bg-white flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 ${
                              isChecked ? 'ring-2 ring-purple-500 bg-purple-50/10' : ''
                            }`}
                          >
                            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4">
                              <div className="flex items-start gap-3 flex-1">
                                <input 
                                  type="checkbox"
                                  checked={isChecked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedIds(prev => [...prev, c.id]);
                                    else setSelectedIds(prev => prev.filter(id => id !== c.id));
                                  }}
                                  className="w-4 h-4 mt-1 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer shrink-0"
                                />
                                <div className="space-y-1.5 flex-1">
                                  <Badge className="bg-amber-100 text-amber-800 border-none font-bold text-[9px] w-fit">{c.category}</Badge>
                                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block pt-1">{t("flashcards.prepositionLabel", { defaultValue: "Preposição:" })} <span className="text-gray-800 font-black">{targetPrep}</span></span>
                                  <h4 className="font-bold text-gray-900 text-xs leading-relaxed">{c.front}</h4>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(c.id);
                                }}
                                className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="pb-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t("flashcards.prepositionRule")}</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleStaticBack(c.id); }}
                                  className="text-amber-600 hover:text-amber-800 font-bold text-[10px] p-1 rounded hover:bg-amber-50 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  {staticVisibleBacks[c.id] ? <><EyeOff className="w-3.5 h-3.5" />{t("flashcards.hideBack")}</> : <><Eye className="w-3.5 h-3.5" />{t("flashcards.viewBack")}</>}
                                </button>
                              </div>

                              {staticVisibleBacks[c.id] ? (
                                <p className="text-xs text-amber-950 font-medium bg-amber-50/50 p-3 rounded-xl border border-amber-100 whitespace-pre-wrap leading-relaxed animate-in fade-in duration-200">
                                  {c.back}
                                </p>
                              ) : (
                                <div className="text-xs text-gray-400 font-medium italic bg-gray-50/80 p-3 rounded-xl border border-gray-100 flex items-center justify-center gap-2 animate-in fade-in duration-200">
                                  <EyeOff className="w-3.5 h-3.5 opacity-50" />
                                  <span>{t("flashcards.verbContext")}</span>
                                </div>
                              )}

                              {targetExample && (
                                <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100 text-[11px]">
                                  <span className="font-bold text-gray-400 block text-[9px] uppercase">{t("flashcards.prepositionContext")}</span>
                                  <p className="text-gray-700 italic font-medium mt-0.5">{targetExample}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          /* LISTAGEM GERAL DE PALAVRAS, FRASES OU TODOS */
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-2 text-gray-500 font-semibold text-xs shrink-0">
                <Filter className="w-4 h-4 text-purple-600" />
                <span>{t("flashcards.filterTitle")}</span>
              </div>
              <div className={`grid gap-3 w-full max-w-3xl ${
                (selectedStudyType === "writing_v2" || filterOrigin === "writing_v2") 
                  ? "grid-cols-1 sm:grid-cols-4" 
                  : "grid-cols-1 sm:grid-cols-3"
              }`}>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.difficulty")}</label>
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-2 border border-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer"
                  >
                    <option value="all">{t("flashcards.allDifficulties")}</option>
                    <option value="easy">{t("flashcards.easy")}</option>
                    <option value="medium">{t("flashcards.medium")}</option>
                    <option value="hard">{t("flashcards.hard")}</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.categoryLabel")}</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-2 border border-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer"
                  >
                    <option value="all">{t("flashcards.allCategories")}</option>
                    {Array.from(new Set(cards.map((c: any) => c.category).filter((cat: any): cat is string => typeof cat === "string" && cat.trim() !== ""))).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.origin")}</label>
                  <select
                    value={filterOrigin}
                    onChange={(e) => setFilterOrigin(e.target.value)}
                    className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-2 border border-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer"
                  >
                    <option value="all">{t("flashcards.allOrigins")}</option>
                    <option value="manual">{t("flashcards.originManual")}</option>
                    <option value="ai">{t("flashcards.originAi")}</option>
                    <option value="writing_v2">{t("flashcards.originV2")}</option>
                  </select>
                </div>
                {(selectedStudyType === "writing_v2" || filterOrigin === "writing_v2") && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">{t("flashcards.section")}</label>
                    <select
                      value={filterSection}
                      onChange={(e) => setFilterSection(e.target.value)}
                      className="bg-gray-50/60 text-gray-700 font-semibold text-xs rounded-xl px-3 py-2 border border-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer"
                    >
                      <option value="all">{t("flashcards.allSections")}</option>
                      <option value="Section A">{t("flashcards.sectionA", "Section A")}</option>
                      <option value="Section B">{t("flashcards.sectionB", "Section B")}</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {(() => {
              const currentFiltered = getFilteredCards();
              const allSelected = currentFiltered.length > 0 && currentFiltered.every(c => selectedIds.includes(c.id));
              const someSelected = currentFiltered.some(c => selectedIds.includes(c.id));

              if (currentFiltered.length === 0) return null;

              return (
                <div className="bg-purple-50/50 border border-purple-100 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox"
                      checked={allSelected}
                      ref={el => {
                        if (el) {
                          el.indeterminate = someSelected && !allSelected;
                        }
                      }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Select all visible
                          const newIds = [...selectedIds];
                          currentFiltered.forEach(c => {
                            if (!newIds.includes(c.id)) newIds.push(c.id);
                          });
                          setSelectedIds(newIds);
                        } else {
                          // Deselect all visible
                          const visibleIds = currentFiltered.map(c => c.id);
                          setSelectedIds(selectedIds.filter(id => !visibleIds.includes(id)));
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="text-xs font-bold text-gray-700">
                      {selectedIds.length > 0 
                        ? t("flashcards.selectedCountMsg", { count: selectedIds.length, total: currentFiltered.length }) 
                        : t("flashcards.selectAllCountMsg", { count: currentFiltered.length })
                      }
                    </span>
                  </div>

                  {selectedIds.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isDeletingBulk}
                      onClick={handleBulkDelete}
                      className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
                    >
                      {isDeletingBulk ? (
                        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                      )}
                      {t("flashcards.deleteSelected", { count: selectedIds.length })}
                    </Button>
                  )}
                </div>
              );
            })()}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(() => {
                const filteredCards = getFilteredCards();

                if (filteredCards.length === 0) {
                  return (
                    <div className="col-span-full py-12 text-center text-gray-400 font-medium text-xs bg-gray-50/30 rounded-2xl border border-dashed border-gray-200">
                      {t("flashcards.noFlashcardsFilteredMsg", { defaultValue: "Nenhum flashcard encontrado com os filtros selecionados." })}
                    </div>
                  );
                }

                return filteredCards.map((card: any) => {
                  const isChecked = selectedIds.includes(card.id);
                  return (
                    <Card 
                      key={card.id} 
                      onClick={() => setSelectedCard(card)}
                      className={`border-none shadow-sm bg-white flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 ${
                        isChecked ? 'ring-2 ring-purple-500 bg-purple-50/10' : ''
                      }`}
                    >
                      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, card.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== card.id));
                              }
                            }}
                            className="w-4 h-4 mt-1 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer shrink-0"
                          />
                          <div className="space-y-1.5 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              {isWritingV2Flashcard(card) ? (
                                <>
                                  <Badge className="bg-purple-100 text-purple-800 border-none font-bold text-[8px] px-1.5 py-0.5 rounded-md">
                                    Expression Écrite
                                  </Badge>
                                  {card.metadata?.section && (
                                    <Badge className="bg-blue-100 text-blue-800 border-none font-bold text-[8px] px-1.5 py-0.5 rounded-md">
                                      {card.metadata.section}
                                    </Badge>
                                  )}
                                  <Badge className="bg-amber-100 text-amber-800 border-none font-bold text-[8px] px-1.5 py-0.5 rounded-md">
                                    {translateErrorCategory(card.category || card.metadata?.category || "Geral", t)}
                                  </Badge>
                                </>
                              ) : (
                                <>
                                  {card.category && (
                                    <Badge variant="outline" className="text-[8px] font-bold border-gray-100 px-1.5 py-0.5 text-gray-500 rounded-md bg-gray-50/30">
                                      {translateErrorCategory(card.category, t)}
                                    </Badge>
                                  )}
                                  {card.source === "writing_ai_correction" && (
                                    <Badge variant="outline" className="text-[8px] font-black border-purple-100 bg-purple-50/50 text-purple-700 px-1.5 py-0.5 rounded-md">
                                      {t("flashcards.originAi")}
                                    </Badge>
                                  )}
                                  {card.type && (
                                    <Badge variant="outline" className="text-[8px] font-black border-blue-100 bg-blue-50/50 text-blue-700 px-1.5 py-0.5 rounded-md uppercase">
                                      {card.type}
                                    </Badge>
                                  )}
                                </>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("flashcards.frontLabel")}</span>
                            <h4 className="font-bold text-gray-900 leading-relaxed font-serif text-sm">{card.front}</h4>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(card.id);
                          }}
                          className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardHeader>
                      <CardContent className="pb-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t("flashcards.backLabel")}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBackVisibility(card.id);
                            }}
                            className="text-gray-400 hover:text-purple-600 transition-colors p-1 rounded-md hover:bg-purple-50 flex items-center gap-1 text-[10px] font-semibold cursor-pointer"
                          >
                            {visibleBacks[card.id] ? <><EyeOff className="w-3.5 h-3.5" />{t("flashcards.hideBack")}</> : <><Eye className="w-3.5 h-3.5" />{t("flashcards.viewBack")}</>}
                          </button>
                        </div>
                        {visibleBacks[card.id] ? (
                          isWritingV2Flashcard(card) ? (
                            renderEEV2Back(card.back)
                          ) : (
                            <p className="text-xs text-gray-700 font-medium whitespace-pre-wrap leading-relaxed bg-purple-50/50 p-3 rounded-xl border border-purple-100 animate-in fade-in duration-200">
                              {card.back}
                            </p>
                          )
                        ) : (
                          <div className="text-xs text-gray-400 font-medium italic bg-gray-50/80 p-3 rounded-xl border border-gray-100 flex items-center justify-center gap-2 animate-in fade-in duration-200">
                            <EyeOff className="w-3.5 h-3.5 opacity-50" />
                            <span>{t("flashcards.hideBackDesc", { defaultValue: "Resposta oculta (clique em Mostrar)" })}</span>
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="pt-2 border-t border-gray-50 flex items-center justify-between pb-4 px-6 bg-gray-50/50">
                        <span className="text-[10px] font-semibold text-gray-400">{t("flashcards.estimatedDifficultyLabel", { defaultValue: "Dificuldade Estimada" })}</span>
                        <Badge className={`text-[9px] px-2 py-0.5 border ${getFlashcardDifficultyBadgeClass(card.difficulty)}`}>
                          {(() => {
                            const diff = normalizeFlashcardDifficulty(card.difficulty);
                            return diff === "easy" ? t("flashcards.easy") : diff === "hard" ? t("flashcards.hard") : t("flashcards.medium");
                          })()}
                        </Badge>
                      </CardFooter>
                    </Card>
                  );
                });
              })()}
            </div>
          </div>
        )}
      </div>

      {/* DETAILED FLASHCARD VIEW / EDIT MODAL FOR PERSONAL CARDS */}
      <ContextModal
        isOpen={!!selectedCard}
        onClose={() => {
          setSelectedCard(null);
          setIsEditing(false);
        }}
        title={isEditing ? t("flashcards.editCardTitle", "Editar Flashcard") : t("flashcards.cardDetailsTitle", "Detalhes do Flashcard")}
        description={isEditing ? t("flashcards.editCardDesc", "Modifique os campos desejados.") : t("flashcards.cardDetailsDesc", "Frente, verso e status de repetição espaçada.")}
        size="md"
      >
        {selectedCard && (
          isEditing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="editFrontText">{t("flashcards.editFrontHelp", "Frente do Card (Termo ou Pergunta)")}</Label>
                <Input
                  id="editFrontText"
                  value={editFront}
                  onChange={(e) => setEditFront(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="editBackText">{t("flashcards.backHelp", "Verso do Card (Significado ou Resposta)")}</Label>
                <Textarea
                  id="editBackText"
                  value={editBack}
                  onChange={(e) => setEditBack(e.target.value)}
                  required
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="editCategoryField">{t("flashcards.categoryLabel", "Categoria")}</Label>
                  <Input
                    id="editCategoryField"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    placeholder={t("flashcards.categoryHelp", "Ex: Vocabulaire")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="editLanguageField">{t("flashcards.languageLabel", "Idioma")}</Label>
                  <select
                    id="editLanguageField"
                    value={editLanguage}
                    onChange={(e) => setEditLanguage(e.target.value)}
                    className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
                  >
                    <option value="French">{t("flashcards.french", "Francês")}</option>
                    <option value="English">{t("flashcards.english", "Inglês")}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="editTypeField">{t("flashcards.cardTypeLabel", "Tipo")}</Label>
                  <select
                    id="editTypeField"
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
                  >
                    <option value="grammar">{t("flashcards.grammarOption", "Gramática")}</option>
                    <option value="text">{t("flashcards.vocabOption", "Texto / Vocabulário")}</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="editDifficultyField">{t("flashcards.difficulty", "Dificuldade")}</Label>
                  <select
                    id="editDifficultyField"
                    value={editDifficulty}
                    onChange={(e) => setEditDifficulty(normalizeFlashcardDifficulty(e.target.value))}
                    className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
                  >
                    <option value="easy">{t("flashcards.easy", "Fácil")}</option>
                    <option value="medium">{t("flashcards.medium", "Médio")}</option>
                    <option value="hard">{t("flashcards.hard", "Difícil")}</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="rounded-xl font-semibold text-xs"
                >
                  {t("questionBank.cancel", "Cancelar")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-md"
                >
                  {isSaving ? t("flashcards.saving", "Salvando...") : t("flashcards.editCardButton", "Salvar Alterações")}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[9px] px-2.5 py-0.5 border ${getFlashcardDifficultyBadgeClass(selectedCard.difficulty)}`}>
                  {t("flashcards.difficulty", "Dificuldade")}: {getFlashcardDifficultyLabel(selectedCard.difficulty, t)}
                </Badge>
                {selectedCard.category && (
                  <Badge variant="outline" className="text-[9px] px-2.5 py-0.5 border-gray-100 text-gray-600 bg-gray-50">
                    {t("flashcards.categoryLabel", "Categoria")}: {translateErrorCategory(selectedCard.category, t)}
                  </Badge>
                )}
                {selectedCard.language && (
                  <Badge variant="outline" className="text-[9px] px-2.5 py-0.5 border-blue-100 text-blue-700 bg-blue-50">
                    {t("flashcards.languageLabel", "Idioma")}: {selectedCard.language === "French" ? t("flashcards.french", "Francês") : selectedCard.language === "English" ? t("flashcards.english", "Inglês") : selectedCard.language}
                  </Badge>
                )}
                {selectedCard.source === "writing_ai_correction" && (
                  <Badge variant="outline" className="text-[9px] px-2.5 py-0.5 border-purple-100 text-purple-700 bg-purple-50">
                    {t("flashcards.origin", "Origem")}: {t("flashcards.originAi", "Correction IA")}
                  </Badge>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 space-y-1.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("flashcards.frontLabel", "Frente (Anverse)")}</span>
                  <p className="text-sm font-bold text-gray-900 leading-relaxed font-serif">{selectedCard.front}</p>
                </div>

                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 space-y-1.5">
                  <span className="text-[10px] text-purple-700 font-bold uppercase tracking-wider block">{t("flashcards.backLabel", "Verso (Resposta)")}</span>
                  <p className="text-sm font-medium text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedCard.back}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 bg-gray-50/30 p-3.5 rounded-xl border border-gray-100/50 text-xs font-semibold">
                <div className="space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("flashcards.statusLabel", "Status de Fixação")}</span>
                  <span className="text-gray-700 font-bold">
                    {normalizeFlashcardDifficulty(selectedCard.difficulty) === "easy" ? t("flashcards.statusEasy", "Aprendido (SRS Consolidado)") :
                     normalizeFlashcardDifficulty(selectedCard.difficulty) === "medium" ? t("flashcards.statusMedium", "Em progresso (Médio)") :
                     t("flashcards.statusHard", "Em revisão (Difficile)")}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("flashcards.nextReviewLabel", "Próxima Revisão")}</span>
                  <span className="text-gray-700 font-bold">
                    {selectedCard.nextReview || t("flashcards.nextReviewToday", "Hoje (Agendamento SRS)")}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex gap-2 justify-between">
                <Button
                  variant="ghost"
                  onClick={() => handleDelete(selectedCard.id)}
                  className="rounded-xl text-red-600 hover:bg-red-50 font-bold text-xs"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  {t("flashcards.deleteCardButton", "Excluir Card")}
                </Button>

                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => startEditCard(selectedCard)}
                    className="rounded-xl border-gray-100 font-semibold text-xs"
                  >
                    {t("flashcards.editCardButton", "Editar Card")}
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </ContextModal>

      {/* FLOATING CREATION FORM MODAL */}
      <ContextModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t("flashcards.newCardTitle", "Criar Novo Flashcard")}
        description={t("flashcards.modalDescription", "Preencha os dados abaixo para gerar um novo flashcard de memorização.")}
        size="md"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="modalFrontText">{t("flashcards.frontHelp", "Frente do Card (Termo ou Pergunta em Francês)")}</Label>
            <Input 
              id="modalFrontText" 
              placeholder={t("flashcards.placeholderFront", "Ex: Le gouvernement français")} 
              value={front} 
              onChange={e => setFront(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="modalBackText">{t("flashcards.backHelp", "Verso do Card (Significado ou Tradução)")}</Label>
            <Textarea 
              id="modalBackText" 
              placeholder={t("flashcards.placeholderBack", "Ex: O governo francês")} 
              value={back} 
              onChange={e => setBack(e.target.value)}
              required
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="modalCategoryField">{t("flashcards.categoryLabel", "Categoria")}</Label>
              <Input
                id="modalCategoryField"
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder={t("flashcards.categoryHelp", "Ex: Grammaire, Vocabulaire, Idiome")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="modalLanguageField">{t("flashcards.languageLabel", "Idioma")}</Label>
              <select
                id="modalLanguageField"
                value={language}
                onChange={e => setLanguage(e.target.value)}
                className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
              >
                <option value="French">{t("flashcards.french", "Francês")}</option>
                <option value="English">{t("flashcards.english", "Inglês")}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="modalTypeField">{t("flashcards.cardTypeLabel", "Tipo")}</Label>
              <select
                id="modalTypeField"
                value={typeField}
                onChange={e => setTypeField(e.target.value)}
                className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
              >
                <option value="grammar">{t("flashcards.grammarOption", "Gramática / Regra")}</option>
                <option value="text">{t("flashcards.vocabOption", "Texto / Vocabulário")}</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="modalDifficultySetting">{t("flashcards.difficultyHelp", "Dificuldade Inicial")}</Label>
              <select
                id="modalDifficultySetting"
                value={difficulty}
                onChange={e => setDifficulty(normalizeFlashcardDifficulty(e.target.value))}
                className="w-full bg-white text-gray-700 font-semibold text-xs rounded-lg p-2.5 border border-gray-200 cursor-pointer"
              >
                <option value="easy">{t("flashcards.easy", "Fácil")}</option>
                <option value="medium">{t("flashcards.medium", "Médio")}</option>
                <option value="hard">{t("flashcards.hard", "Difícil")}</option>
              </select>
            </div>
          </div>

          <Button type="submit" disabled={isSaving} className="w-full bg-primary hover:bg-primary/95 text-white rounded-xl shadow-md py-5 font-bold">
            {isSaving ? t("flashcards.saving", "Criando...") : t("flashcards.saveCardButton", "Criar Flashcard")}
          </Button>
        </form>
      </ContextModal>
    </div>
  );
}
